import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner, 
  Table,
  Badge,
  Dropdown
} from 'react-bootstrap';
import { locationService, ParkingLocation, LocationFormData, LocationsResponse } from '../../services/locationService';
import './LocationSettings.css';

const LocationSettings: React.FC = () => {
  const [locations, setLocations] = useState<ParkingLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ParkingLocation | null>(null);
  const [floorPlanFile, setFloorPlanFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<LocationFormData>({
    areaDescription: '',
    parkingLot: '默認車場',
    parentLocation: '',
    totalSpaces: 9999,
    actualCapacity: 9999,
    isChargeable: false,
    notes: ''
  });

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateTarget, setGenerateTarget] = useState<ParkingLocation | null>(null);
  const [generateError, setGenerateError] = useState<string>('');
  const [generateLoading, setGenerateLoading] = useState<boolean>(false);
  const [generateForm, setGenerateForm] = useState({
    floor: '1F',
    zone: '',
    counts: {
      standard: 0,
      compact: 0,
      large: 0,
      handicapped: 0,
      electric: 0,
      vip: 0,
      visitor: 0
    }
  });

  // 載入地點數據
  const loadLocations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await locationService.getLocations();
      
      if (response.success && response.data) {
        // 後端返回的數據結構是 { success: true, data: { locations: [...], pagination: {...} } }
        const locationsData = response.data.locations || response.data;
        setLocations(Array.isArray(locationsData) ? locationsData : []);
      } else {
        throw new Error(response.message || '載入地點數據失敗');
      }
    } catch (error: any) {
      console.error('載入地點失敗:', error);
      setError(error.message || '載入地點數據失敗，請稍後再試');
      setLocations([]); // 確保在錯誤情況下 locations 是空數組
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');

      // 前端基本驗證：數值範圍與邏輯
      if (!formData.areaDescription || formData.areaDescription.trim().length === 0) {
        setError('請輸入區域描述');
        return;
      }
      if (formData.totalSpaces < 1 || formData.actualCapacity < 1) {
        setError('車位數與實際可在場車輛數必須大於等於 1');
        return;
      }
      if (formData.actualCapacity > formData.totalSpaces) {
        setError('實際可在場車輛數不能超過總車位數');
        return;
      }
      
      if (editingLocation) {
        await locationService.updateLocation(editingLocation._id, formData, floorPlanFile || undefined);
        setSuccess('地點更新成功');
      } else {
        await locationService.createLocation(formData, floorPlanFile || undefined);
        setSuccess('地點創建成功');
      }
      
      setShowModal(false);
      resetForm();
      loadLocations();
    } catch (error: any) {
      console.error('提交失敗:', error);
      setError(error.message || '操作失敗，請稍後再試');
    }
  };

  // 重置表單
  const resetForm = () => {
    setFormData({
      areaDescription: '',
      parkingLot: '默認車場',
      parentLocation: '',
      totalSpaces: 9999,
      actualCapacity: 9999,
      isChargeable: false,
      notes: ''
    });
    setFloorPlanFile(null);
    setEditingLocation(null);
  };

  // 打開新增模態框
  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  // 打開編輯模態框
  const handleEdit = (location: ParkingLocation) => {
    setEditingLocation(location);
    setFormData({
      areaDescription: location.areaDescription,
      parkingLot: location.parkingLot,
      parentLocation: location.parentLocation || '',
      totalSpaces: location.totalSpaces,
      actualCapacity: location.actualCapacity,
      isChargeable: location.isChargeable,
      notes: location.notes || ''
    });
    setFloorPlanFile(null);
    setShowModal(true);
  };

  // 開啟生成車位模態窗
  const openGenerateModal = (location: ParkingLocation) => {
    setGenerateTarget(location);
    setGenerateForm({
      floor: '1F',
      zone: location.areaDescription,
      counts: {
        standard: 0,
        compact: 0,
        large: 0,
        handicapped: 0,
        electric: 0,
        vip: 0,
        visitor: 0
    }
  });
  setGenerateError('');
  setShowGenerateModal(true);
  };

  // 送出生成車位
  const submitGenerateSpots = async () => {
    if (!generateTarget) return;
    try {
      setGenerateLoading(true);
      setGenerateError('');
      const payload = {
        floor: generateForm.floor,
        zone: generateForm.zone,
        counts: generateForm.counts
      };
      const resp = await locationService.generateSpots(generateTarget._id, payload);
      if (resp.success) {
        setSuccess(`車位生成成功：新增 ${resp.data?.createdCount || 0} 個 (${resp.data?.floor} / ${resp.data?.zone})`);
        setShowGenerateModal(false);
        setGenerateTarget(null);
        loadLocations();
      } else {
        setGenerateError(resp.message || '生成車位失敗');
      }
    } catch (error: any) {
      setGenerateError(error.message || '生成車位失敗，請稍後再試');
    } finally {
      setGenerateLoading(false);
    }
  };

  // 刪除地點
  const handleDelete = async (locationId: string) => {
    if (!window.confirm('確定要刪除此地點嗎？')) return;
    
    try {
      await locationService.deleteLocation(locationId);
      setSuccess('地點刪除成功');
      loadLocations();
    } catch (error: any) {
      console.error('刪除失敗:', error);
      setError(error.message || '刪除失敗，請稍後再試');
    }
  };

  // 預覽平面圖（內嵌顯示，不下載）
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const handlePreviewFloorPlan = async (location: ParkingLocation) => {
    if (!location.floorPlan) return;
    const id = location._id;
    // 若已在預覽，則收合並釋放資源
    if (previewUrls[id]) {
      try {
        window.URL.revokeObjectURL(previewUrls[id]);
      } catch {}
      setPreviewUrls(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    // 取得 Blob 並建立預覽 URL
    try {
      const blob = await locationService.getFloorPlan(id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrls(prev => ({ ...prev, [id]: url }));
    } catch (error: any) {
      console.error('預覽平面圖失敗:', error);
      setError(error.message || '預覽平面圖失敗');
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="location-settings">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>地點設定</h2>
            <Button variant="primary" onClick={handleAdd}>
              ➕ 新增地點
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">地點列表</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>區域描述</th>
                    <th>所屬車場</th>
                    <th>從屬於</th>
                    <th>車位數</th>
                    <th>實際可在場車輛數</th>
                    <th>是否收費</th>
                    <th>平面圖</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted">
                        暫無地點數據
                      </td>
                    </tr>
                  ) : (
                    locations.map((location) => (
                      <>
                        <tr key={location._id}>
                          <td>{location.areaDescription}</td>
                          <td>{location.parkingLot}</td>
                          <td>{location.parentLocation || '無上級'}</td>
                          <td>{location.totalSpaces}</td>
                          <td>{location.actualCapacity}</td>
                          <td>
                            <Badge bg={location.isChargeable ? 'success' : 'secondary'}>
                              {location.isChargeable ? '收費' : '免費'}
                            </Badge>
                          </td>
                          <td>
                            {location.floorPlan ? (
                              <Button
                                variant={previewUrls[location._id] ? 'outline-secondary' : 'outline-info'}
                                size="sm"
                                onClick={() => handlePreviewFloorPlan(location)}
                              >
                                {previewUrls[location._id] ? '收合' : '查看'}
                              </Button>
                            ) : (
                              <span className="text-muted">無</span>
                            )}
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="outline-secondary" size="sm">
                                操作
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => openGenerateModal(location)}>
                                  ⚙️ 生成車位
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleEdit(location)}>
                                  ✏️ 編輯
                                </Dropdown.Item>
                                <Dropdown.Item 
                                  onClick={() => handleDelete(location._id)}
                                  className="text-danger"
                                >
                                  🗑️ 刪除
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                        {previewUrls[location._id] && (
                          <tr>
                            <td colSpan={8}>
                              <div className="mt-2 mb-3">
                                <img
                                  src={previewUrls[location._id]}
                                  alt={`${location.areaDescription} 平面圖`}
                                  style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 新增/編輯模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingLocation ? '編輯地點' : '新增地點'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>區域描述 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.areaDescription}
                    onChange={(e) => setFormData({...formData, areaDescription: e.target.value})}
                    required
                    placeholder="請輸入區域描述"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>所屬車場</Form.Label>
                  <Form.Select
                    value={formData.parkingLot}
                    onChange={(e) => setFormData({...formData, parkingLot: e.target.value})}
                  >
                    <option value="默認車場">默認車場</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>從屬於</Form.Label>
                  <Form.Select
                    value={formData.parentLocation}
                    onChange={(e) => setFormData({...formData, parentLocation: e.target.value})}
                  >
                    <option value="">無上級</option>
                    {locations
                      .filter(loc => loc._id !== editingLocation?._id)
                      .map(loc => (
                        <option key={loc._id} value={loc._id}>
                          {loc.areaDescription}
                        </option>
                      ))
                    }
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車位數</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.totalSpaces}
                    onChange={(e) => setFormData({...formData, totalSpaces: parseInt(e.target.value || '1', 10)})}
                    min="1"
                    max="9999"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>實際可在場車輛數</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.actualCapacity}
                    onChange={(e) => setFormData({...formData, actualCapacity: parseInt(e.target.value || '1', 10)})}
                    min="1"
                    max="9999"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>是否收費</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="是否收費"
                    checked={formData.isChargeable}
                    onChange={(e) => setFormData({...formData, isChargeable: e.target.checked})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>備註</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="請輸入備註信息"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>停車場平面圖</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      setFloorPlanFile(file || null);
                    }}
                  />
                  <Form.Text className="text-muted">
                    支持圖片格式（JPG, PNG, GIF）和 PDF 文件
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button variant="primary" type="submit">
              {editingLocation ? '更新' : '確定'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* 生成車位模態窗 */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>生成車位 - {generateTarget?.areaDescription}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generateError && (
            <Alert variant="danger" dismissible onClose={() => setGenerateError('')}>{generateError}</Alert>
          )}
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>樓層</Form.Label>
                  <Form.Control
                    type="text"
                    value={generateForm.floor}
                    onChange={(e) => setGenerateForm({...generateForm, floor: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>區域</Form.Label>
                  <Form.Control
                    type="text"
                    value={generateForm.zone}
                    onChange={(e) => setGenerateForm({...generateForm, zone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>標準車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.standard}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, standard: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>小型車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.compact}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, compact: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>大型車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.large}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, large: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>電動車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.electric}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, electric: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>無障礙車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.handicapped}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, handicapped: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>VIP 車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.vip}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, vip: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>訪客車位</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={generateForm.counts.visitor}
                    onChange={(e) => setGenerateForm({...generateForm, counts: { ...generateForm.counts, visitor: parseInt(e.target.value || '0', 10) }})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={submitGenerateSpots} disabled={generateLoading}>
            {generateLoading ? '生成中...' : '生成車位'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LocationSettings;